import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AlbumCatalogue, AlbumFiguresMap, AlbumFontPair, AlbumInk, AlbumMap, AlbumMapStyle, AlbumView, TripSummary, WorldMapData } from '../../types';
import AlbumMapView from './AlbumMapView';
import { fontCss } from '../../utils/albumFonts';
import { statsApi } from '../../api/stats.api';
import { fmtUTCDate } from '../../utils/dateTime';
import { getDateLocale } from '../../utils/dateLocale';

interface Props {
  summary: TripSummary | null;
  trip: AlbumView['trip'];
  style: AlbumMapStyle;
  /** ADR-0048: the ink that reads on this page's background. */
  ink: AlbumInk;
  /** ADR-0048: the album's typefaces — the headline and figures take the title's. */
  fonts: AlbumFontPair;
  frame: AlbumCatalogue['figuresMap'];
  /** ADR-0049: the page shrinks whole with its format, and its margin with it. */
  scale: number;
  margin: number;
  figuresMap: AlbumFiguresMap;
  /** Off: the stops alone. Old albums and older servers send no field — the route shows. */
  figuresRoute?: boolean;
  /** Off: no country under the route. Missing (older servers) → drawn. */
  figuresOutline?: boolean;
  /** The visited countries' outline, drawn by the server (ADR-0047); only for `COUNTRY`. */
  countryMap: AlbumMap | undefined;
  /** Page width in mm: everything below is laid out in mm of the printed page. */
  pageWidth: number;
}

// Every thumbnail of the album draws this page: one request for the geometry, not one each.
let worldMap: Promise<WorldMapData | null> | null = null;
const loadWorldMap = () => (worldMap ??= statsApi.worldMap().catch(() => null));

const MM_PER_PT = 0.3528;

/**
 * The trip in figures as an album page: who and when at the top, the distance
 * as the headline, the rest in a row, and where in the world. A figure the
 * server didn't send — or a zero — is left out rather than printed.
 */
export default function AlbumStatsPage({ summary, trip, style, ink, fonts, frame, scale, margin, figuresMap, figuresRoute = true, figuresOutline = true, countryMap, pageWidth }: Props) {
  const { t, i18n } = useTranslation();
  const [world, setWorld] = useState<WorldMapData | null>(null);
  useEffect(() => { if (figuresMap === 'WORLD') void loadWorldMap().then(setWorld); }, [figuresMap]);

  if (!summary) return null;
  const mm = (v: number) => `${(v / pageWidth) * 100}cqw`;
  const sp = (v: number) => mm(v * scale);
  const pt = (v: number) => sp(v * MM_PER_PT);
  const locale = getDateLocale(i18n.language);

  const hero = summary.km !== null && summary.km > 0
    ? { value: summary.km.toLocaleString(i18n.language), unit: t('stats.km'), caption: t('album.figures.kmCaption') }
    : { value: String(summary.days), unit: '', caption: t('stats.days', { count: summary.days }) };
  const figures = [
    summary.km !== null && summary.km > 0 ? { key: 'days', value: summary.days, label: t('stats.days', { count: summary.days }) } : null,
    { key: 'stops', value: summary.stops, label: t('stats.stops', { count: summary.stops }) },
    { key: 'countries', value: summary.countries.length, label: t('stats.countries', { count: summary.countries.length }) },
    summary.photos ? { key: 'photos', value: summary.photos, label: t('stats.photos', { count: summary.photos }) } : null,
    summary.entries ? { key: 'entries', value: summary.entries, label: t('stats.entries', { count: summary.entries }) } : null,
  ].filter((f): f is { key: string; value: number; label: string } => !!f && f.value > 0);
  const visited = new Set(summary.countries);

  return (
    // ADR-0049: when the map sits to the right (landscape), the figures keep to its left.
    <div className="absolute inset-0 flex flex-col" style={{
      padding: mm(margin), color: ink.text, ...fontCss(fonts.body),
      paddingRight: frame.x > pageWidth / 2 ? mm(pageWidth - frame.x + 8 * scale) : mm(margin),
    }}>
      <p className="font-mono uppercase" style={{ fontSize: pt(7), letterSpacing: '0.2em', color: style.tones[0] }}>
        {t('album.statsTitle')}
      </p>
      {trip && (
        <>
          <p className="leading-[1.05]" style={{ fontSize: pt(30), marginTop: sp(4), ...fontCss(fonts.title) }}>
            {trip.name}
          </p>
          <p style={{ fontSize: pt(9), marginTop: sp(3), color: ink.muted }}>
            {trip.destination} · {fmtUTCDate(trip.startDate, 'd MMM', locale)} – {fmtUTCDate(trip.endDate, 'd MMM yyyy', locale)}
          </p>
        </>
      )}

      <div style={{ marginTop: sp(22) }}>
        <p className="tabular-nums leading-none" style={{ fontSize: pt(76), ...fontCss(fonts.title), fontStyle: 'normal' }}>
          {hero.value}
          {hero.unit && <span style={{ fontSize: pt(26), marginLeft: sp(2), ...fontCss(fonts.title), fontWeight: 400 }}>{hero.unit}</span>}
        </p>
        <p className="uppercase" style={{ fontSize: pt(7.5), letterSpacing: '0.18em', marginTop: sp(3), color: ink.muted }}>
          {hero.caption}
        </p>
      </div>

      <div className="grid" style={{
        gridTemplateColumns: `repeat(${Math.max(figures.length, 1)}, minmax(0, 1fr))`,
        marginTop: sp(12), paddingTop: sp(6), borderTop: `${sp(0.3)} solid ${style.coast}`,
      }}>
        {figures.map(f => (
          <div key={f.key}>
            <p className="tabular-nums leading-none" style={{ fontSize: pt(26), ...fontCss(fonts.title), fontStyle: 'normal' }}>
              {f.value.toLocaleString(i18n.language)}
            </p>
            <p className="uppercase" style={{ fontSize: pt(6.5), letterSpacing: '0.16em', marginTop: sp(2), color: ink.muted }}>
              {f.label}
            </p>
          </div>
        ))}
      </div>

      {/* The map sits in the catalogue's box, so the PDF puts it in the same place. */}
      <div className="absolute" style={{ left: mm(frame.x), top: mm(frame.y), width: mm(frame.w), height: mm(frame.h) }}>
        {figuresMap === 'COUNTRY'
          ? countryMap && <AlbumMapView map={{ ...countryMap, routes: figuresRoute ? countryMap.routes : [], land: figuresOutline ? countryMap.land : '' }} style={style} />
          : world && (
            <svg viewBox={`0 0 ${world.width} ${world.height}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet" aria-hidden>
              {Object.entries(world.countries).map(([code, d]) => (
                <path key={code} d={d} fill={visited.has(code) ? style.tones[0] : style.land} stroke={visited.has(code) ? style.tones[0] : style.coast} strokeWidth={0.4} />
              ))}
            </svg>
          )}
      </div>
    </div>
  );
}
